import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap, Marker, Popup } from 'react-leaflet';
import L, { Icon } from 'leaflet';
import MarkerBigIcon from 'leaflet/dist/images/marker-icon-2x.png';
import MarkerStandartIcon from 'leaflet/dist/images/marker-icon.png';
import smallBlueIcon from '../../img/icons8-circled-notch-12-blue.png';
import smallGreenIcon from '../../img/icons8-circled-notch-12-green.png';

import 'leaflet/dist/leaflet.css';
import './MapPlaceholder.css';

// Компонент для автоматического зума и центрирования
const AutoFitBounds = ({ markers }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!markers || markers.length === 0) return;
    
    // Фильтруем валидные маркеры с координатами
    const validMarkers = markers.filter(
      marker => marker?.contacts?.address?.lat && marker?.contacts?.address?.lon
    );
    
    if (validMarkers.length === 0) {
      // Если нет валидных маркеров, показываем дефолтную позицию
      map.setView([50.447978, 30.522384], 10);
      return;
    }
    
    if (validMarkers.length === 1) {
      // Для одного маркера: центрируем и устанавливаем зум 15-16 (хорошо видны улицы)
      const marker = validMarkers[0];
      map.setView([marker.contacts.address.lat, marker.contacts.address.lon], 15);
    } else {
      // Для нескольких маркеров: вычисляем bounds и подгоняем с отступами
      const bounds = validMarkers.map(marker => [
        marker.contacts.address.lat,
        marker.contacts.address.lon
      ]);
      
      // Создаем bounds и применяем с отступами (padding)
      const latLngBounds = L.latLngBounds(bounds);
      
      // Разные отступы в зависимости от количества маркеров
      let padding;
      if (validMarkers.length <= 3) {
        padding = [50, 50]; // маленький отступ для 2-3 маркеров
      } else if (validMarkers.length <= 10) {
        padding = [80, 80]; // средний отступ для 4-10 маркеров
      } else {
        padding = [100, 100]; // большой отступ для многих маркеров
      }
      
      map.fitBounds(latLngBounds, { padding });
    }
  }, [map, markers]);
  
  return null;
};

// Компонент для управления масштабом при изменении количества маркеров
const ZoomControl = ({ markers }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!markers || markers.length === 0) return;
    
    const validMarkers = markers.filter(
      marker => marker?.contacts?.address?.lat && marker?.contacts?.address?.lon
    );
    
    if (validMarkers.length === 1) {
      const marker = validMarkers[0];
      // Плавный зум к маркеру
      map.flyTo([marker.contacts.address.lat, marker.contacts.address.lon], 17);
    } else if (validMarkers.length > 1) {
      const bounds = validMarkers.map(marker => [
        marker.contacts.address.lat,
        marker.contacts.address.lon
      ]);
      const latLngBounds = L.latLngBounds(bounds);
      map.flyToBounds(latLngBounds, {
        padding: validMarkers.length <= 3 ? [50, 50] : [80, 80],
        duration: 1 // анимация 1 секунда
      });
    }
  }, [map, markers]);
  
  return null;
};

// Кнопка для повноекранного режиму
const FullScreenControl = () => {
  const map = useMap();
  
  const toggleFullScreen = () => {
    const container = map.getContainer();
    
    if (!document.fullscreenElement) {
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.msRequestFullscreen) {
        container.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  return (
    <div className="leaflet-top leaflet-right">
      <div className="leaflet-control">
        <button 
          className="fullscreen-toggle" 
          onClick={toggleFullScreen}
          title="Розгорнути на весь екран"
        >
          ⛶
        </button>
      </div>
    </div>
  );
};

// Перераховує розміри карти при зміні контейнера
// (зміна layout top/side, поява/зникнення сусідніх блоків тощо)
const InvalidateOnResize = ({ layout }) => {
  const map = useMap();

  // Реагуємо на явну зміну режиму розташування (top/side)
  useEffect(() => {
    // невелика затримка, щоб дочекатись завершення CSS-переходу/перерендеру
    const t1 = setTimeout(() => map.invalidateSize(), 50);
    const t2 = setTimeout(() => map.invalidateSize(), 350); // ще раз після transition: all .3s
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [map, layout]);

  // Підстраховка: слідкуємо за реальними змінами розміру контейнера карти
  useEffect(() => {
    const container = map.getContainer();
    if (!container || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      map.invalidateSize();
    });
    ro.observe(container);

    return () => ro.disconnect();
  }, [map]);

  return null;
};

const MapPlaceholder = ({ kglist = [], layout = 'top', onMarkerClick, hoveredKgId = null }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [mapReady, setMapReady] = useState(false);

  // Інстанси Leaflet-маркерів за _id садочка — щоб програмно відкривати/закривати
  // попап при наведенні на картку в списку (зворотний бік handleMarkerClick)
  const markerRefs = useRef({});
  const prevHoveredKgIdRef = useRef(null);

  useEffect(() => {
    const prevId = prevHoveredKgIdRef.current;

    if (prevId && prevId !== hoveredKgId) {
      const prevMarker = markerRefs.current[prevId];
      if (prevMarker) prevMarker.closePopup();
    }

    if (hoveredKgId) {
      const marker = markerRefs.current[hoveredKgId];
      if (marker) marker.openPopup();
    }

    prevHoveredKgIdRef.current = hoveredKgId;
  }, [hoveredKgId]);
  
  // Оптимизация: создаем иконку один раз, вне зависимости от количества маркеров
  const getIconForMarker = (index) => {
    const totalCount = kglist.length;
    if (totalCount < 3) {
      return new Icon({ iconUrl: MarkerBigIcon });
    } else if (totalCount > 10) {
      return new Icon({ iconUrl: smallBlueIcon });
    } else {
      return new Icon({ iconUrl: MarkerStandartIcon });
    }
  };
  
  const defaultPosition = [50.447978, 30.522384];
  
  // Обробник подій повноекранного режиму
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('msfullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('msfullscreenchange', handleFullScreenChange);
    };
  }, []);

  // Фильтруем маркеры с валидными координатами
  const validMarkers = kglist.filter(
    kg => kg?.contacts?.address?.lat && kg?.contacts?.address?.lon
  );

  return (
    <div className={`map-container ${isFullScreen ? 'fullscreen' : ''} layout-${layout}`}>
      <MapContainer 
        center={defaultPosition} 
        zoom={10}
        minZoom={8}
        maxZoom={18}
        scrollWheelZoom={true}
        className="map-placeholder"
        whenReady={() => setMapReady(true)}
      >
        <TileLayer
          attribution='&copy; iefrstudio powered <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Автоматическое масштабирование при загрузке */}
        <AutoFitBounds markers={validMarkers} />
        
        {/* Плавное масштабирование при изменении списка маркеров */}
        <ZoomControl markers={validMarkers} />
        
        <FullScreenControl />

        {/* Корректировка размеров карты при смене layout (top/side) и ресайзе контейнера */}
        <InvalidateOnResize layout={layout} />
        
        {validMarkers.map((kg, index) => {
          const numberStr = kg.number != null ? String(kg.number).trim() : '';
          const isShortNumber = numberStr.length > 0 && numberStr.length <= 2;

          return (
          <Marker 
            key={kg._id}
            position={[kg.contacts.address.lat, kg.contacts.address.lon]}
            icon={getIconForMarker(index)} 
            ref={(el) => {
              if (el) markerRefs.current[kg._id] = el;
              else delete markerRefs.current[kg._id];
            }}
            eventHandlers={{
              click: () => onMarkerClick && onMarkerClick(kg._id),
            }}
          >
            <Popup className="kg-map-popup" minWidth={260} maxWidth={320}>
              <div className="kg-popup">
                {numberStr && (
                  <div className={`kg-popup-number ${isShortNumber ? 'kg-popup-number-circle' : 'kg-popup-number-pill'}`}>
                    {numberStr}
                  </div>
                )}
                <a href={`object/${kg._id}`} className="kg-popup-info">
                  <div className="kg-popup-address">
                    <svg className="kg-popup-pin" width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 21s7-7.29 7-12a7 7 0 1 0-14 0c0 4.71 7 12 7 12Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
                      <circle cx="12" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.8"/>
                    </svg>
                    <span>{kg.contacts.address.string}</span>
                  </div>
                </a>
              </div>
            </Popup>
          </Marker>
        )})}
      </MapContainer>
      
      {!isFullScreen && validMarkers.length > 0 && (
        <div className="map-hint">
          {false && <small>
            {validMarkers.length === 1 
              ? "Один садок. Масштаб наближено для детального перегляду" 
              : `Знайдено ${validMarkers.length} садків. Карта автоматично масштабована для їх перегляду`
            }  
          </small>}
          <small>Натисніть ⛶ для повноекранного перегляду</small>
        </div>
      )}
    </div>
  );
};

export default MapPlaceholder;